import { forwardRef, useLayoutEffect } from "react";
import { m } from "framer-motion";
import { OverlayProps } from "./overlay-props";
import { useOverlayViewport } from "./use-overlay-viewport";
import { Underlay } from "./underlay";
import { FocusScope } from "@react-aria/focus";
import { useObjectRef } from "@react-aria/utils";
import { isMobileApp } from "@common/core/platform-detection";
import {
  getOverlayContainerZIndex,
  MODAL_SURFACE_Z_INDEX,
  OVERLAY_LOCAL_SURFACE_Z_INDEX,
  OVERLAY_LOCAL_UNDERLAY_Z_INDEX,
} from "./overlay-z-index";

export const Modal = forwardRef<HTMLDivElement, OverlayProps>(
  (
    {
      children,
      autoFocus = false,
      restoreFocus = true,
      isDismissable = true,
      placement = "center",
      zIndex,
      onClose,
    },
    ref,
  ) => {
    const viewPortStyle = useOverlayViewport();
    const objRef = useObjectRef(ref);
    const isNativeMobileApp = isMobileApp();
    const surfaceZIndex = zIndex ?? MODAL_SURFACE_Z_INDEX;
    const style = {
      ...viewPortStyle,
      zIndex: getOverlayContainerZIndex(surfaceZIndex),
    };
    const initialAnimation = {
      opacity: 0,
      scale: placement === "top" ? 1 : 0.7,
    };
    const animateAnimation = { opacity: 1, scale: 1 };
    const exitAnimation = { opacity: 0, scale: 1 };
    const transition = { duration: 0.1 };

    useLayoutEffect(() => {
      if (!autoFocus) {
        return;
      }

      const animationFrame = window.requestAnimationFrame(() => {
        objRef.current
          ?.querySelector<HTMLElement>('[role="dialog"]')
          ?.focus({ preventScroll: true });
      });

      return () => {
        window.cancelAnimationFrame(animationFrame);
      };
    }, [autoFocus, objRef]);

    return (
      <div
        className="fixed inset-0 isolate"
        style={style}
        onKeyDown={(e) => {
          if (e.key === "Escape") {
            e.stopPropagation();
            e.preventDefault();
            onClose();
          }
        }}
      >
        <Underlay
          key="modal-underlay"
          zIndex={OVERLAY_LOCAL_UNDERLAY_Z_INDEX}
          onClick={() => {
            if (isDismissable) {
              onClose();
            }
          }}
        />
        <m.div
          ref={objRef}
          className={[
            "pointer-events-none absolute inset-0 flex h-full w-full",
            placement === "center" ? "items-center justify-center" : "",
            placement === "top" ? "items-start justify-center pt-40" : "",
            isNativeMobileApp ? "native-overlay-motion-surface" : "",
          ]
            .filter(Boolean)
            .join(" ")}
          style={{
            zIndex: OVERLAY_LOCAL_SURFACE_Z_INDEX,
            willChange: isNativeMobileApp ? "transform, opacity" : undefined,
            backfaceVisibility: isNativeMobileApp ? "hidden" : undefined,
            WebkitBackfaceVisibility: isNativeMobileApp
              ? "hidden"
              : undefined,
            contain: isNativeMobileApp ? ("paint" as const) : undefined,
          }}
          role="presentation"
          initial={initialAnimation}
          animate={animateAnimation}
          exit={exitAnimation}
          transition={transition}
          transformTemplate={(_, generatedTransform) =>
            generatedTransform
              ? `${generatedTransform} translateZ(0)`
              : "translateZ(0)"
          }
        >
          <FocusScope restoreFocus={restoreFocus} autoFocus={false} contain>
            {children}
          </FocusScope>
        </m.div>
      </div>
    );
  },
);
